import type { SetRequired } from 'type-fest';

import type {
  Fn,
  Fn0,
  Fn1,
  Fn2,
  Fn3,
  Fn4,
  Fn5,
  Fn6,
  Fn7,
  Fn8,
  Fn9,
  Fn10,
  JsonObject,
  JsonValue,
  Predicate,
} from '#types.js';

import { isArray, isBoolean, isDate, isError, isNumber, isRegExp, isString, isSymbol, merge } from 'lodash-es';

import { assert, DetailedError, errorMessage } from '#errors.js';
import { isFn, isJsonScalar, isPromiseLike, isRecord, isUnknownArray } from '#types.js';
import { mapEntries, matchesPattern, shorten } from '#utils.js';
import { isZodType, zodTypeName } from '#zod/types.js';
import { zodToJson } from '#zod-utils.ts';

export type ValueTransformer<From = unknown, To extends JsonValue = JsonValue> = (
  value: From,
  args: { key: string; opts: ResolvedSimplifyOpts },
) => To;

export interface SimplifyOpts {
  maxDepth?: number;
  maxArrayLength?: number;
  maxStringLength?: number;
  undefinedFunctions?: boolean;
  asIsKeys?: Array<string | RegExp>;
  redact?: ValueTransformer;
  mapValue?: ValueTransformer;
  tooDeep?: MaxDepthValueStrategy;
  asString?: boolean;
  key?: string;
  omitKeys?: Array<string | RegExp>;
  detectZod?: boolean;
  detectLlmJson?: boolean;
  objectIdentifiers?: string[];

  noCustomInspect?: boolean;

  /** @internal */
  __visited?: Set<unknown>;
}

export type ResolvedSimplifyOpts = SetRequired<SimplifyOpts, keyof SimplifyDefaults | '__visited'>;

/**
 * Stringifies a value, optionally with a custom replacer function.
 * Limits the depth of the object to the specified number of maxDepth.
 * Replaces the redacted values with output of the redact function if it returns a non-nullish or false value.
 * @param value - The value to stringify.
 * @param options - The options for the stringification.
 * @param options.maxDepth - The maximum depth of the object to stringify.
 * @param options.redact - A function that returns a new value for the key if it should be redacted.
 * @returns The stringified value.
 */
export function simplify<T>(value: T, overrides: SimplifyOpts = {}): (T & JsonValue) | undefined {
  const opts = {
    ...simplify.defaults,
    __visited: new Set<unknown>(),
    ...overrides,
    omitKeys: [...simplify.defaults.omitKeys, ...(overrides.omitKeys ?? [])],
    asIsKeys: [...simplify.defaults.asIsKeys, ...(overrides.asIsKeys ?? [])],
  } satisfies ResolvedSimplifyOpts;
  if (typeof value === 'object' && value !== null) {
    if (opts.__visited.has(value)) return;
    opts.__visited.add(value);
  }
  const preparedValue = prepare({ key: 'root', value, depth: 0, opts });
  if (preparedValue === undefined) return;
  return preparedValue as T & JsonValue;
}

simplify.defaults = {
  key: 'root',
  tooDeep: 'excerpt',
  undefinedFunctions: true,
  maxStringLength: 512,
  maxDepth: 10,
  maxArrayLength: 20,
  omitKeys: [/^[#_$]/, 'logger', 'genkit'],
  asIsKeys: ['id', '_id', '$id', 'uuid', 'slug', 'key', 'name', 'title', 'path', 'href', 'ref', 'type', 'url', 'iri'],
  objectIdentifiers: [
    'id',
    '_id',
    '$id',
    'uuid',
    'slug',
    'key',
    'name',
    'title',
    'path',
    'href',
    'ref',
    'type',
    'url',
    'iri',
  ],
} satisfies SimplifyOpts;

export type SimplifyDefaults = typeof simplify.defaults;

export type JsonSerializer<From> = (value: From) => JsonValue;

export const serializer = <From>(serializeFn: JsonSerializer<From>): JsonSerializer<From> => serializeFn;

export type MaxDepthBuiltInStrategy = 'describe' | 'drop' | 'excerpt' | 'keep' | 'toString' | 'toJsonString' | 'error';

export type MaxDepthValueStrategy = MaxDepthBuiltInStrategy | SimplifyOpts | ValueTransformer;

export type DescribeValueOpts = Pick<
  SimplifyOpts,
  'undefinedFunctions' | 'maxStringLength' | 'maxArrayLength' | 'objectIdentifiers'
>;

export function describeValue(value: unknown, overrides: Partial<DescribeValueOpts> = {}): string | undefined {
  const defaults = describeValue.defaults;
  const opts = merge({}, defaults, overrides) satisfies DescribeValueOpts;
  const { undefinedFunctions, maxStringLength, maxArrayLength, objectIdentifiers } = opts;
  if (typeof value === 'function') {
    if (undefinedFunctions) return;
    return `${value.name ? ` ${value.name}` : 'function'}()`;
  } else if (typeof value === 'string') {
    return shorten(value, { maxLength: maxStringLength, includeOmittedLength: true });
  } else if (typeof value === 'number') {
    return String(value);
  } else if (typeof value === 'boolean') {
    return String(value);
  } else if (value === undefined) {
    return 'undefined';
  } else if (value === null) {
    return 'null';
  } else if (isZodType(value)) {
    return `[z.${zodTypeName(value)}]`;
  } else if (value instanceof Date) {
    return value.toISOString();
  } else if (value instanceof URL) {
    return value.toString();
  } else if (value instanceof Error) {
    return errorMessage(value);
  } else if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const buf = `[${value.map((item) => describeValue(item, opts)).join(', ')}`;
    if (value.length <= maxArrayLength) {
      return `${buf}]`;
    } else {
      return `${buf}... <skipping ${value.length - maxArrayLength} more elements>]`;
    }
  } else if (typeof value === 'object') {
    if (Object.keys(value).length === 0) return '{}';
    if ('describe' in value) {
      let desc: string;
      if (isFn(0, value.describe)) {
        const objDesc = value.describe();
        assert(typeof objDesc === 'string', `describe() method on ${value.constructor.name} must return a string`);
        desc = objDesc;
      } else {
        assert(
          typeof value.describe === 'string',
          `describe field/getter on ${value.constructor.name} must be a string`,
        );
        desc = value.describe;
      }
      if (desc.trim().length > 0) return desc;
    }
    const attrs = Object.getOwnPropertyNames(value).filter((key) => {
      if (objectIdentifiers.includes(key)) return false;
      if (typeof value[key as keyof typeof value] === 'function') return false;
      if (value[key as keyof typeof value] === undefined) return false;
      return true;
    });
    const name = value.constructor?.name || 'Object';
    let buf = `${name}(`;
    for (const key of objectIdentifiers) {
      if (key in value) {
        buf += `${describeValue(value[key as keyof typeof value], { maxStringLength })}: `;
        break;
      }
    }
    buf += `${attrs.length > 10 ? `${attrs.length} props` : attrs.join(', ')})`;
    return buf;
  } else {
    return `[${typeof value}]`;
  }
}

describeValue.defaults = simplify.defaults satisfies DescribeValueOpts;

export type DescribeValueDefaults = typeof describeValue.defaults;

simplify.maxDepthValueStrategies = {
  describe: (value, { opts }) => describeValue(value, opts),
  drop: () => undefined,
  excerpt: (value, { opts }) =>
    simplify(value, {
      ...opts,
      tooDeep: 'drop',
      maxDepth: Math.floor(opts.maxDepth / 2),
      maxStringLength: Math.floor(opts.maxStringLength / 2),
      maxArrayLength: Math.floor(opts.maxArrayLength / 2),
    }),
  keep: (value) => value as JsonValue,
  toString: String,
  toJsonString: (value) => JSON.stringify(value, null, 2),
  error: (value, { key, opts }) => {
    throw new DetailedError(`Max depth reached at key "${key}": ${describeValue(value)}`, { details: { key, opts } });
  },
} as const satisfies Record<MaxDepthBuiltInStrategy, ValueTransformer>;

simplify.defaultValueTransformer = ((value, { key, opts }) => {
  const customValue = opts.noCustomInspect ? customSimplified(value) : undefined;
  if (customValue) return customValue;
  if (isZodType(value)) return zodToJson(value);
  if (value instanceof Error) return errorMessage(value);
  if (value instanceof Date) return value.toISOString();
  if (value instanceof URL) return value.toString();
  return;
}) satisfies ValueTransformer;

function resolveMaxDepthValueStrategy(strategy: MaxDepthValueStrategy): ValueTransformer {
  if (typeof strategy === 'function') return strategy;
  if (typeof strategy === 'object') return (value) => simplify(value, strategy);
  const fn = simplify.maxDepthValueStrategies[strategy];
  assert(fn, `Invalid max depth value strategy: ${strategy}`);
  return fn;
}

function prepare({
  key,
  value,
  depth,
  opts,
}: {
  key: string;
  value: unknown;
  depth: number;
  opts: ResolvedSimplifyOpts;
}): JsonValue {
  if (value === undefined || value === null) return value;
  const defaults = simplify.defaults;
  opts = { ...defaults, ...opts };
  const {
    asIsKeys,
    omitKeys,
    maxDepth,
    redact,
    tooDeep,
    mapValue,
    undefinedFunctions,
    maxStringLength,
    maxArrayLength,
  } = opts;
  const maxDepthReplacer = resolveMaxDepthValueStrategy(tooDeep);
  if (matchesPattern(key, asIsKeys)) return value as JsonValue;
  if (matchesPattern(key, omitKeys)) return;
  if (redact) {
    const redactedValue = redact(value, { key, opts });
    if (redactedValue !== undefined) return redactedValue;
  }
  if (value === undefined || value === null) return value;
  const overwrittenValue = mapValue?.(value, { key, opts }) ?? simplify.defaultValueTransformer(value, { key, opts });
  if (overwrittenValue !== undefined) return overwrittenValue;
  if (depth >= maxDepth) {
    const newVal = maxDepthReplacer(value, { key, opts });
    if (newVal === undefined) return;
    return simplify(newVal, { ...opts, maxDepth: 0, tooDeep: 'drop' });
  }
  if (isFn(value)) {
    return undefinedFunctions ? undefined : describeValue(value);
  }
  if (typeof value === 'symbol' || typeof value === 'bigint') return String(value);
  if (isNumber(value) || isBoolean(value)) return value;
  if (isString(value)) return shorten(value, { maxLength: maxStringLength, includeOmittedLength: true });
  if (isDate(value)) return value.toISOString();
  if (isRegExp(value)) return value.toString();
  if (isError(value)) return errorMessage(value);
  if (isSymbol(value)) return value.toString();
  if (isUnknownArray(value)) {
    if (value.length === 0) return [];
    if (depth >= maxDepth) return describeValue(value);
    let scopedVal = value;
    if (value.length > maxArrayLength) {
      scopedVal = scopedVal.slice(0, maxArrayLength);
      scopedVal.push(`...${value.length - maxArrayLength} more`);
    }
    return scopedVal.map((item) => prepare({ key, value: item, depth: depth + 1, opts }));
  }
  if (typeof value === 'object') {
    const plainObject = value;
    if (depth < maxDepth) {
      const ret = {} as Record<string, JsonValue>;
      for (const [subKey, value] of Object.entries(plainObject)) {
        const newValue = prepare({ key: subKey, value, depth: depth + 1, opts });
        if (newValue === undefined) continue;
        ret[subKey] = newValue;
      }
      return ret;
    } else {
      return describeValue(plainObject);
    }
  }

  if (isJsonSerializable(value)) return value;
  return `<${value.constructor?.name ?? 'UnknownObject'}>`;
}

export function simplifyJsonObject<T extends object>(obj: T | undefined): (T & JsonObject) | undefined {
  if (obj === undefined) return;
  const ret = simplify(obj);
  if (ret === undefined) return;
  assert(typeof ret === 'object', `Simplified object is not an object: ${typeof ret} (${describeValue(ret)})`);
  return ret as T & JsonObject;
}

export function stringify(value: unknown, opts: SimplifyOpts = {}): string {
  if (typeof value === 'string') return value;
  const simplified = simplify(value, opts);
  try {
    return JSON.stringify(simplified, null, 2);
  } catch {
    return String(value);
  }
}

export const customInspectSym = Symbol.for('nodejs.util.inspect.custom');

export function customSimplified(value: unknown): JsonValue | undefined {
  if (!(typeof value === 'function' || typeof value === 'object') || !value) return;
  const simplifyOpts = objectKey('$simplify').getFrom<SimplifyOpts | boolean>(value);
  if (simplifyOpts === undefined || simplifyOpts === false) return;
  const opts = { ...(simplifyOpts === true ? undefined : simplifyOpts), noCustomInspect: true } satisfies SimplifyOpts;
  return simplify(value, opts);
}

export function isNonPlainObject(value: unknown): value is object {
  if (value === null || typeof value !== 'object') return false;

  const proto = Object.getPrototypeOf(value);
  return proto !== null && Object.getPrototypeOf(proto) !== null;
}

export function isJsonSerializable(value: unknown): value is JsonValue {
  try {
     
    JSON.parse(JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function toPlainJson(
  value: unknown,
  { fallback }: { fallback?: JsonValue } = {},
  __visited = new Set<unknown>(),
): JsonValue {
  if (__visited.has(value)) return fallback;
  else __visited.add(value);
  if (isJsonScalar(value)) return value;
  if (isArray(value)) return value.map((v) => toPlainJson(v, { fallback }, __visited));
  if (isRecord(value)) return mapEntries(value, (key, value) => [key, toPlainJson(value, { fallback }, __visited)]);
  return fallback;
}

export function isLargeValue(value: unknown, { thresholdChars = 1024 }: { thresholdChars?: number } = {}): boolean {
  try {
    const stringified = JSON.stringify(value);
    return stringified.length > thresholdChars;
  } catch {
    return false;
  }
}

export function objectKey<const Key extends string, Value>(
  key: Key,
  predicate?: Predicate<Value>,
): ObjectKey<Key, Value> {
  return new ObjectKey(key, predicate);
}

export const alwaysTrue = <V>(value: unknown): value is V => true;

export class ObjectKey<const Key extends string, Value = unknown> {
  constructor(
    readonly name: Key,
    readonly predicate: Predicate<Value> = alwaysTrue,
  ) {}

  isPresentIn = <V = Value>(obj: unknown): obj is { [key in Key]-?: V } => {
    if (!obj) return false;
    if (!(typeof obj === 'object' || typeof obj === 'function')) return false;
    return obj[this.name as keyof typeof obj] !== undefined;
  };
  isValidIn = <V = Value>(obj: unknown): obj is { [key in Key]-?: V } => {
    if (!obj) return false;
    if (!(['object', 'function'].includes(typeof obj) && obj[this.name as keyof typeof obj])) return false;
    const value = obj[this.name as keyof typeof obj];
    if (value === undefined) return false;
    return this.predicate(value);
  };
  isMissingIn = <V = Value>(obj: unknown): obj is { [key in Key]?: V | undefined | void | null } =>
    !this.isPresentIn(obj);
  assertPresentIn = <V = Value>(obj: unknown): asserts obj is { [key in Key]-?: V } => {
    assert(this.isPresentIn<V>(obj), `Key ${this.name} not found in the object: ${typeof obj}`);
  };

  isMethodIn<R = void>(obj: unknown, arity: 0): obj is { [key in Key]-?: Fn0<R> };
  isMethodIn<A1, R = void>(obj: unknown, arity: 1): obj is { [key in Key]-?: Fn1<A1, R> };
  isMethodIn<A1, A2, R = void>(obj: unknown, arity: 2): obj is { [key in Key]-?: Fn2<A1, A2, R> };
  isMethodIn<A1, A2, A3, R = void>(obj: unknown, arity: 3): obj is { [key in Key]-?: Fn3<A1, A2, A3, R> };
  isMethodIn<A1, A2, A3, A4, R = void>(obj: unknown, arity: 4): obj is { [key in Key]-?: Fn4<A1, A2, A3, A4, R> };
  isMethodIn<A1, A2, A3, A4, A5, R = void>(
    obj: unknown,
    arity: 5,
  ): obj is { [key in Key]-?: Fn5<A1, A2, A3, A4, A5, R> };
  isMethodIn<A1, A2, A3, A4, A5, A6, R = void>(
    obj: unknown,
    arity: 6,
  ): obj is { [key in Key]-?: Fn6<A1, A2, A3, A4, A5, A6, R> };
  isMethodIn<A1, A2, A3, A4, A5, A6, A7, R = void>(
    obj: unknown,
    arity: 7,
  ): obj is { [key in Key]-?: Fn7<A1, A2, A3, A4, A5, A6, A7, R> };
  isMethodIn<A1, A2, A3, A4, A5, A6, A7, A8, R = void>(
    obj: unknown,
    arity: 8,
  ): obj is { [key in Key]-?: Fn8<A1, A2, A3, A4, A5, A6, A7, A8, R> };
  isMethodIn<A1, A2, A3, A4, A5, A6, A7, A8, A9, R = void>(
    obj: unknown,
    arity: 9,
  ): obj is { [key in Key]-?: Fn9<A1, A2, A3, A4, A5, A6, A7, A8, A9, R> };
  isMethodIn<A1, A2, A3, A4, A5, A6, A7, A8, A9, A10, R = void>(
    obj: unknown,
    arity: 10,
  ): obj is { [key in Key]-?: Fn10<A1, A2, A3, A4, A5, A6, A7, A8, A9, A10, R> };
  isMethodIn<Args extends unknown[], R = void>(
    obj: unknown,
    arity?: Args['length'] | undefined,
  ): obj is { [key in Key]-?: Fn<Args, R> };
  isMethodIn<R>(obj: unknown): obj is { [key in Key]-?: Fn0<R> };
  isMethodIn(obj: unknown, arity?: number) {
    const value = this.getFrom(obj);
    if (!value) return false;
    if (isFn(arity, value)) return this.predicate(value);
    return false;
  }

  getFrom = <V = Value>(obj: unknown): V | undefined => {
    if (!this.isPresentIn(obj)) return;
    const value = obj[this.name] as unknown as V;
    if (typeof value === 'function') return value.bind(obj) as V;
    else return value;
  };
  readFrom = <V = Value>(obj: unknown): V | undefined => {
    const value = this.getFrom<V | Fn0<V>>(obj);
    if (value === undefined) return;
    if (isFn(value)) {
      // Get the value from the function, unless it's async or needs arguments
      if (value.length === 0) {
        const ret = value();
        if (isPromiseLike(ret)) {
          throw new DetailedError(
            `Promise returned from the function ${value.name} when trying to read the key ${this.name} from ${obj}`,
            { cause: ret, details: { key: this.name, object: describeValue(obj) } },
          );
        }
        return ret;
      } else {
        throw new DetailedError(`Non-zero arity function returned from the key ${this.name} from ${obj}`, {
          cause: value,
          details: { key: this.name, object: describeValue(obj) },
        });
      }
    } else {
      // If the value is not a function, it should be a value or a getter, but not a promise
      if (isPromiseLike(value)) {
        throw new DetailedError(`Promise returned from the key ${this.name} from ${obj}`, {
          cause: value,
          details: { key: this.name, object: describeValue(obj) },
        });
      }
      return value;
    }
  };
}
